import { Alert, Grid, Paper, Snackbar, Stack } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import { Company } from '../models/CompanyModel';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import EditTools from '../components/forms/EditTools';
import TextInput from '../components/forms/TextInput';
import { useEndpoints } from '../utils/EndpointContext';
import { useParams } from 'react-router-dom';
import { FileModel } from '../models/FileModel';
import { defaultValues } from '../models/defaultValues';
import { EditableContext } from '../contexts/EditableContext';

type Props = {}
type Params = {
    companyCode: string;
    productType: string;
    versionId: string;
};

const CompaniesPage: React.FC = (props: Props) => {

    const { companyCode, versionId } = useParams<Params>();

    const endPoints = useEndpoints();
    const itemsEndRef = useRef<null | HTMLDivElement>(null);
    const [itemAdded, setItemAdded] = useState(false);
    const [savedData, setSavedData] = useState<FileModel>();
    const [companies, setCompanies] = useState<Company[]>();
    const [openAlert, setOpenAlert] = useState<boolean>(false);

    const isEditable = !savedData?.DatePublished;

    useEffect(() => {
        axios.get(`${endPoints.questionEditorRead}`,{
            params: {
                partitionKey: companyCode,
                rowKey: versionId,
                downloadData: true
            }
        })
        .then(response => {
            setSavedData(response.data);
            setCompanies(response.data.Data!.Companies);
        });
    }, []);

    useEffect(() => {
        if (itemAdded && itemsEndRef.current) {
          itemsEndRef.current.scrollIntoView({ behavior: 'smooth' });
          setItemAdded(false);
        }
    }, [itemAdded]);

    const handleUpdateKey = (value: string, index: number) => {
        setCompanies(prevData => 
            prevData!.map((item, i) => 
                i === index ? { ...item, Id: value } : item
            )
        );
    };
    
    const handleUpdateName = (value: string, index: number) => {
        setCompanies(prevData => 
            prevData!.map((item, i) => 
                i === index ? { ...item, Name: value } : item
            )
        );
    };
    
    const handleUpdateAddress = (value: string, index: number) => {
        setCompanies(prevData => 
            prevData!.map((item, i) => 
                i === index ? { ...item, Address: value } : item
            )
        );
    };

    const handleCreate = () => {
        setCompanies(prevData => [...prevData!, { ...defaultValues.company }]);
        setItemAdded(true);
    };

    const handleDelete = (index: number) => {
        setCompanies(prevData => prevData!.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        try {
            savedData!.Data!.Companies = companies!;
            await axios.post(`${endPoints.questionEditorUpdate}`, savedData);
        } catch (error) {
            console.log(error);
        } finally {
            setOpenAlert(true);
        }
    };

    const handleAlertClose = () => {
        setOpenAlert(false);
    }

    return(
        <div>
            {companies ?
            <EditableContext.Provider value={isEditable}>
                <EditTools onSave={handleSave} onCreate={handleCreate} />
                <Stack spacing={2}>
                    {companies.map((item, index) => (
                        <Paper key={index}>
                            <Stack>
                                <Grid container spacing={2} sx={{mb: "1em"}}>
                                    <Grid item xs={3}>
                                        <TextInput label={'Key'} fieldName={'key'} value={item.Id || ''} onChange={(value) => handleUpdateKey(value, index)} />
                                    </Grid>
                                    <Grid item xs={8}>
                                        <TextInput label={'Name'} fieldName={'Name'} value={item.Name || ''} onChange={(value) => handleUpdateName(value, index)} />
                                    </Grid>
                                    {isEditable &&
                                        <Grid item xs={1} className='actions-bar'>
                                            <Button color='error' onClick={() => handleDelete(index)}> <DeleteIcon /></Button>
                                        </Grid>
                                    }
                                </Grid>
                            </Stack>
                            <TextInput label={'Address'} multiline={true} fieldName={'address'} value={item.Address} onChange={(value) => handleUpdateAddress(value, index)} />
                        </Paper>
                    ))}
                </Stack>
                <div ref={itemsEndRef}></div>
                <EditTools onSave={handleSave} onCreate={handleCreate} />
                <Snackbar open={openAlert} autoHideDuration={6000} onClose={handleAlertClose}>
                    <Alert severity="success" sx={{ width: '100%' }}>
                        Companies updated successfully.
                    </Alert>
                </Snackbar>
            </EditableContext.Provider>
            : <>Loading Companies</>}
        </div>
    );
}

export default CompaniesPage;